import { parseDateIso } from '@/utils/operationReportDisplay';

/**
 * Претензии в листах учёта (задача 10833): счёт претензий по листам для колонки списка
 * листов, переход из карточки листа в форму претензии и проверка строки листа в форме.
 */

/** Строка ответа списка претензий: `sheet_ids` есть всегда, `seen` — для пользователя сессии. */
export interface PretensionListRow {
  id: number;
  sheet_ids?: number[];
  seen?: boolean;
}

export interface UnreadOfTotal {
  unread: number;
  total: number;
}

/** Непрочитанные и все претензии каждого листа. Без признака `seen` претензия непрочитана. */
export function pretensionCountsBySheet(
  rows: PretensionListRow[],
  sheetIds: number[],
): Record<number, UnreadOfTotal> {
  const counts: Record<number, UnreadOfTotal> = {};
  sheetIds.forEach((sheetId) => {
    counts[sheetId] = { unread: 0, total: 0 };
  });
  rows.forEach((row) => {
    new Set((row.sheet_ids || []).map(Number)).forEach((sheetId) => {
      const count = counts[sheetId];
      if (!count) {
        return;
      }
      count.total += 1;
      if (row.seen !== true) {
        count.unread += 1;
      }
    });
  });
  return counts;
}

/** Ячейка «непрочитанные/всего»; 0/0 — пустая ячейка. */
export function unreadOfTotalCell(unread: unknown, total: unknown): string {
  const all = Number(total);
  if (!Number.isFinite(all) || all <= 0) {
    return '';
  }
  const fresh = Number(unread);
  return `${Number.isFinite(fresh) && fresh > 0 ? fresh : 0}/${all}`;
}

/**
 * Параметры адреса страницы претензий, которые заполняют форму. Период — не dateFrom/dateTo:
 * их читает фильтр «Дата создания претензии» и запомнил бы период листа как фильтр.
 */
export const PRETENSION_PREFILL_KEYS = ['sheetId', 'platformId', 'supplierId', 'periodFrom', 'periodTo'];

export interface PretensionPrefill {
  sheetId: number | null;
  platformId: string | null;
  supplierId: string | null;
  period: string[] | null;
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/** Предзаполнение из адреса; каждый параметр независим. null — параметров нет вовсе. */
export function pretensionPrefillFromQuery(query: Record<string, unknown>): PretensionPrefill | null {
  if (!PRETENSION_PREFILL_KEYS.some((key) => query[key] !== undefined)) {
    return null;
  }
  const text = (key: string): string | null => {
    const value = query[key];
    return typeof value === 'string' && value.trim() !== '' ? value.trim() : null;
  };
  const sheetId = Number(text('sheetId'));
  const from = text('periodFrom');
  const to = text('periodTo');
  return {
    sheetId: Number.isInteger(sheetId) && sheetId > 0 ? sheetId : null,
    platformId: text('platformId'),
    supplierId: text('supplierId'),
    period: from && to && ISO_DATE.test(from) && ISO_DATE.test(to) ? [from, to] : null,
  };
}

export function queryWithoutPrefill<T extends Record<string, unknown>>(query: T): T {
  const rest: Record<string, unknown> = { ...query };
  PRETENSION_PREFILL_KEYS.forEach((key) => {
    delete rest[key];
  });
  return rest as T;
}

/** Адрес формы претензии из карточки листа: лист, его площадка, поставщик и период. */
export function sheetPretensionQuery(sheet: {
  id: number;
  platformId?: number | null;
  supplierId?: number | null;
  startDate: string;
  finishDate: string;
}): Record<string, string> {
  const query: Record<string, string> = { sheetId: String(sheet.id) };
  if (sheet.platformId) {
    query.platformId = String(sheet.platformId);
  }
  if (sheet.supplierId) {
    query.supplierId = String(sheet.supplierId);
  }
  const from = parseDateIso(sheet.startDate);
  const to = parseDateIso(sheet.finishDate);
  if (from && to) {
    query.periodFrom = from;
    query.periodTo = to;
  }
  return query;
}

/** Площадка, поставщик и статус листа, по которым форма сверяет строку. */
export interface SheetOwner {
  platformId: number | string | null;
  supplierId: number | string | null;
  status?: string | null;
}

/**
 * Статусы листа, по которым претензию выпускать нельзя (решение 35 плана): отозванный лист
 * не действует, оплаченный закрыт расчётом, ждущий оплаты уже передан на оплату
 * бухгалтерии. Ту же проверку делает сервис претензий.
 */
const CLOSED_SHEET_REASONS: Record<string, string> = {
  recalled: 'Лист отозван — претензию по нему выпустить нельзя',
  payment_await: 'Лист ждёт оплаты — претензию по нему выпустить нельзя',
  payment_done: 'Лист оплачен — претензию по нему выпустить нельзя',
};

/**
 * Почему по листу нельзя выпустить претензию; null — можно. returned — претензия возвращена на
 * доработку: её уже выпускали, лист «Ждем оплаты» закрыт только для новых.
 */
export function sheetClosedReason(status: unknown, returned = false): string | null {
  if (returned && status === 'payment_await') {
    return null;
  }
  return CLOSED_SHEET_REASONS[String(status ?? '')] ?? null;
}

/**
 * Ошибка строки листа в форме претензии (решение 9 плана): null — строка в порядке.
 * owner: undefined — лист ещё не загружали, null — листа с таким id нет.
 */
export function sheetRowError(
  rows: Array<{ sheetId?: unknown }>,
  index: number,
  owner: SheetOwner | null | undefined,
  platformId: unknown,
  supplierId: unknown,
  returned = false,
): string | null {
  const raw = rows[index]?.sheetId;
  const sheetId = raw === null || raw === undefined ? '' : String(raw).trim();
  if (sheetId === '') {
    return 'Укажите ID листа';
  }
  if (!/^\d+$/.test(sheetId) || Number(sheetId) <= 0) {
    return 'ID листа — целое число больше нуля';
  }
  if (rows.findIndex((row) => String(row.sheetId ?? '').trim() === sheetId) !== index) {
    return 'Лист уже есть в претензии';
  }
  if (owner === null) {
    return 'Лист не найден';
  }
  if (!owner) {
    return null;
  }
  const differs = (sheetValue: unknown, formValue: unknown) => sheetValue !== null && sheetValue !== undefined
    && formValue !== null && formValue !== undefined && String(sheetValue) !== String(formValue);
  if (differs(owner.supplierId, supplierId)) {
    return 'Лист другого поставщика';
  }
  if (differs(owner.platformId, platformId)) {
    return 'Лист другой площадки';
  }
  return sheetClosedReason(owner.status, returned);
}
